import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  DEFAULT_SETTINGS,
  SETTINGS_KEY,
  SETTINGS_VERSION,
  getLocalStorage,
  setLocalStorage,
  type Settings,
} from './settingsUtils';

export interface SettingsContextType {
  settings: Settings;
  updateSettings: (updates: Partial<Settings>) => void;
  isLoading: boolean;
}

export const SettingsContext = createContext<
  SettingsContextType | undefined
>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load settings from localStorage
  useEffect(() => {
    const loadSettings = () => {
      try {
        // Load settings from localStorage
        const savedSettings = getLocalStorage(SETTINGS_KEY);

        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings);

            // Validate version compatibility
            if (parsed.version === SETTINGS_VERSION) {
              setSettings({ ...DEFAULT_SETTINGS, ...parsed });
            } else {
              // Handle version migration if needed
              console.log(
                `Settings version mismatch: ${parsed.version} vs ${SETTINGS_VERSION}`
              );
              setSettings(DEFAULT_SETTINGS);
            }
          } catch (error) {
            console.warn('Failed to parse saved settings:', error);
            setSettings(DEFAULT_SETTINGS);
          }
        } else {
          // No saved settings, use defaults
          setSettings(DEFAULT_SETTINGS);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Persist settings when they change
  useEffect(() => {
    if (!isLoading) {
      try {
        const settingsJson = JSON.stringify(settings);
        setLocalStorage(SETTINGS_KEY, settingsJson);
      } catch (error) {
        console.error('Error saving settings:', error);
      }
    }
  }, [settings, isLoading]);

  const updateSettings = useCallback((updates: Partial<Settings>) => {
    setSettings((prev) => {
      const newSettings = { ...prev, ...updates };

      // Log settings changes for observability (no PII) - only in development
      if (import.meta.env.DEV) {
        console.log('Settings updated:', {
          timestamp: new Date().toISOString(),
          changes: Object.keys(updates),
          askPatEnabled: newSettings.beta.askPat,
          theme: newSettings.ui.theme,
          colorBlind: newSettings.ui.colorBlind,
        });
      }

      return newSettings;
    });
  }, []);

  const value: SettingsContextType = {
    settings,
    updateSettings,
    isLoading,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
